import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { getAccessToken } from "../utils/axios";
import { BaseClient, type PermissionsResponse } from "../utils/BaseClient";

interface UseSyncPermissionsParams {
  workspaceId?: string;
  orgId?: string;
}

/**
 * Permissions used to live only in the in-memory app context, and were fetched
 * when an entity was selected (entities hall / topbar dropdowns). On a page
 * reload or a direct URL hit that selection never happens, so the context
 * stayed empty and permission driven UI (e.g. the create protocol button) was
 * hidden. Fetching them through the query cache instead keys them to the
 * workspace/org they were verified against, so every consumer on the route
 * shares a single request and a reload re-hydrates them.
 *
 * @param workspaceId Workspace the operations are verified against.
 * @param orgId Org the operations are verified against.
 * @returns the verify response for the given workspace/org.
 */
export const useSyncPermissions = ({
  workspaceId,
  orgId,
}: UseSyncPermissionsParams): UseQueryResult<PermissionsResponse> => {
  const accessToken = getAccessToken();
  const query = useQuery({
    enabled: !!accessToken && !!workspaceId && !!orgId,
    queryKey: ["permissions", workspaceId, orgId],
    queryFn: async (): Promise<PermissionsResponse> => {
      const client = new BaseClient(accessToken);
      const permissions = await client.getPermissions(
        workspaceId ?? "",
        orgId ?? ""
      );

      return permissions ?? {};
    },
  });
  return query;
};
